import { getComponentFromName } from '@folklore/utils';
import { useTransition, animated } from '@react-spring/web';
import classNames from 'classnames';
import isNumber from 'lodash-es/isNumber';
import isObject from 'lodash-es/isObject';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useRoute } from 'wouter';

import useElementSize from '../../hooks/useElementSize';
import useFormatImageUrl from '../../hooks/useFormatImageUrl';
import {
    useFormSubmission,
    useAnswerFormSubmission,
    useIdentifyFormSubmission,
} from '../../hooks/useSubmissions';
import { getImageUrl } from '../../lib/getImageSize';

import BasicButton from '../../components/buttons/BasicButton';
import * as SlideComponents from './slides';
import useSurveySlides from './useSurveySlides';

import styles from './survey.module.css';

interface SurveyProps {
    title: string;
    intro?: SurveySlide;
    form?: Form;
    subscriptions?: string[] | null;
    basePath?: string | null;
    className?: string;
    onStart?: () => void;
    onAnswer?: (slide: SurveySlide, newAnswer) => void;
    onSlideChange?: (slideSurvey: SurveySlide) => void;
}

function Survey({
    title,
    intro,
    form = null,
    subscriptions = null,
    basePath = null,
    className,
    onStart = null,
    onAnswer = null,
    onSlideChange = null,
}: SurveyProps) {
    const formatImageUrl = useFormatImageUrl();
    // Routing
    const withRouter = basePath !== null;
    const [, setLocation] = useLocation();
    const [, params] = useRoute<{ slideIndex?: string }>(
        `${withRouter ? `${basePath.split('?')[0]}/:slideIndex?` : ''}`,
    );
    const { slideIndex: routeSlideIndex = null } = params || {};

    // Form and submission
    const { id: formId = null } = form || {};
    const { submission, isLoading: isLoadingSubmission } = useFormSubmission(formId);
    const { data: submissionData } = submission || {};
    const { answers = null } = submissionData || {};
    const { answerAsync } = useAnswerFormSubmission(formId);
    const { identifyAsync } = useIdentifyFormSubmission(formId);
    const [errors, setErrors] = useState<{ [key: string]: string[] }>({});

    // Slides
    const slides = useSurveySlides({
        intro,
        form,
        routeSlideIndex,
        basePath,
        answers,
        errors,
    });
    // Latest slides, since an answer can change the questions shown after it
    const slidesRef = useRef<SurveySlide[]>(slides);
    slidesRef.current = slides;
    const [slideId, setSlideId] = useState(slides[0].id);
    const currentSlide = withRouter
        ? slides[routeSlideIndex !== null ? parseInt(routeSlideIndex, 10) : 0]
        : slides.find(({ id }) => id === slideId);
    const { id: currentSlideId, type: currentSlideType, group, image = null } = currentSlide || {};
    const currentSlideIndex = slides.findIndex(({ id }) => id === currentSlideId);

    // Transitions
    const lastSlideIndexRef = useRef<number>(-1);
    const transitions = useTransition(currentSlide, {
        initial: { x: '0%' },
        from: ({ id: enterId }) => {
            const enterIndex = slides.findIndex(({ id }) => id === enterId);
            return { x: enterIndex < lastSlideIndexRef.current ? '-100%' : '100%' };
        },
        enter: { x: '0%' },
        leave: ({ id: leaveId }) => {
            const leaveIndex = slides.findIndex(({ id }) => id === leaveId);
            lastSlideIndexRef.current = leaveIndex;
            return { x: leaveIndex > currentSlideIndex ? '100%' : '-100%' };
        },
        keys: ({ id }) => id,
    });

    const imageTransitions = useTransition(currentSlideType === 'question' ? image : null, {
        initial: { opacity: 1 },
        from: { opacity: 0 },
        enter: (it) => (it !== null ? { opacity: 1, zIndex: 2 } : {}),
        leave: (it) => (it !== null ? { opacity: 1, zIndex: 1 } : {}),
        keys: (it) => (it || {}).id,
    });

    const gotoSlide = useCallback(
        (slide: SurveySlide | number | string) => {
            const { current: slides } = slidesRef;
            let slideIndex = null;
            if (isObject(slide)) {
                slideIndex = slides.findIndex(({ id }) => id === slide.id);
            } else if (isNumber(slide)) {
                slideIndex = slide;
            } else {
                slideIndex = slides.findIndex(({ id }) => id === slide);
            }
            if (withRouter) {
                setLocation(slideIndex > 0 ? `${basePath}/${slideIndex}` : basePath);
            } else {
                setSlideId(slides[slideIndex].id);
            }

            if (onSlideChange !== null) {
                onSlideChange(slides[slideIndex]);
            }
        },
        [setLocation, basePath, withRouter, onSlideChange],
    );

    useEffect(() => {
        if (
            !isLoadingSubmission &&
            submission === null &&
            routeSlideIndex !== null &&
            formId !== null
        ) {
            gotoSlide(0);
        }
    }, [isLoadingSubmission, formId]);

    const gotoPreviousSlide = useCallback(
        (fromSlide) => {
            const { id: fromId } = fromSlide || {};
            const previousSlide =
                slides.findIndex(({ id }) => id === (fromId || currentSlideId)) - 1;
            gotoSlide(Math.max(previousSlide, 0));
        },
        [slides, currentSlideId, gotoSlide],
    );

    const gotoNextSlide = useCallback(
        (fromSlide) => {
            const { current: slides } = slidesRef;
            const { id: fromId } = fromSlide || {};
            const nextIndex = slides.findIndex(({ id }) => id === (fromId || currentSlideId)) + 1;
            gotoSlide(Math.min(nextIndex, slides.length - 1));
        },
        [currentSlideId, gotoSlide],
    );

    const gotoNextUnfilledSlide = useCallback(
        (fromSlide) => {
            const unfilledSlides = slides.filter(
                ({ type = null, answer = null }) => type === 'question' && answer === null,
            );
            if (unfilledSlides.length > 0) {
                gotoSlide(unfilledSlides[0]);
            } else {
                gotoNextSlide(fromSlide);
            }
        },
        [slides, gotoSlide, gotoNextSlide],
    );

    const onSlideCompleted = useCallback(
        (slide: SurveySlide) => {
            const { type: slideType } = slide || {};
            if (slideType === 'intro') {
                gotoNextUnfilledSlide(slide);
            } else {
                gotoNextSlide(slide);
            }

            if (slideType === 'intro' && onStart !== null) {
                onStart();
            }
        },
        [gotoNextSlide, gotoNextUnfilledSlide, onStart],
    );

    const onSlideAnswer = useCallback(
        (slide: SurveySlide, newAnswer) => {
            const { field } = slide;
            const { name: fieldName } = field;

            if (onAnswer !== null) {
                onAnswer(slide, newAnswer);
            }

            setErrors({
                [fieldName]: null,
            });

            return answerAsync({
                url:
                    basePath !== null
                        ? `${window.location.protocol}//${window.location.host}${basePath}`
                        : null,
                field: fieldName,
                answer: newAnswer,
            })
                .then(({ submission }) => {
                    const { data } = submission || {};
                    const { answers } = data || {};
                    setTimeout(() => {
                        gotoNextSlide(slide);
                    }, 500);
                    return answers.find(({ field: answerField }) => answerField === fieldName);
                })
                .catch((error) => {
                    if (error.name === 'ValidationError') {
                        const { errors: validationErrors = null } = error.getResponseData();
                        const { answer: answerErrors = null } = validationErrors || {};
                        if (answerErrors !== null) {
                            setErrors({
                                [fieldName]: answerErrors,
                            });
                        }
                    }
                    return null;
                });
        },
        [basePath, answerAsync, gotoNextSlide, onAnswer],
    );

    const onIdentify = useCallback(() => {
        identifyAsync();
    }, [identifyAsync]);

    const { label: groupLabel = null } = group || {};

    const { ref: headerRef, height: headerHeight } = useElementSize();
    const { ref: imagesRef, width: imagesWidth, height: imagesHeight } = useElementSize();

    const hasImage = image !== null && currentSlideType === 'question';

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.hasImage]: hasImage,
                    [styles.entered]: currentSlideType !== 'intro',
                },
                className,
            ])}
            style={{
                '--survey-images-width':
                    imagesWidth !== null && hasImage ? `${imagesWidth}px` : '0px',
                '--survey-images-height':
                    imagesHeight !== null && hasImage ? `${imagesHeight}px` : '0px',
                '--survey-header-height': headerHeight !== null ? `${headerHeight}px` : null,
            }}
        >
            <div className={styles.header} ref={headerRef}>
                <h1 className={styles.title}>
                    {basePath !== null ? <Link href={basePath}>{title}</Link> : title}
                </h1>
                <div className={styles.progress}>
                    {slides
                        .filter(({ type }) => type === 'question')
                        .map((slide) => {
                            const { answer = null } = slide;
                            const index = slides.findIndex(({ id }) => id === slide.id);
                            return (
                                <BasicButton
                                    href={withRouter ? `${basePath}/${index}` : null}
                                    key={slide.id}
                                    className={classNames([
                                        styles.button,
                                        {
                                            [styles.active]: slide.id === currentSlideId,
                                            [styles.completed]: answer !== null,
                                        },
                                    ])}
                                    onClick={!withRouter ? () => setSlideId(slide.id) : null}
                                >
                                    <span className={styles.bar} />
                                    <span className={styles.label}>{index + 1}</span>
                                </BasicButton>
                            );
                        })}
                </div>
                <div className={styles.group}>{groupLabel}</div>
            </div>
            <div className={styles.slides}>
                {transitions((style, slide) => {
                    const { type } = slide;
                    const SlideComponent = getComponentFromName(SlideComponents, type, 'question');
                    return SlideComponent !== null ? (
                        <animated.div
                            style={style}
                            className={classNames([
                                styles.slideContainer,
                                {
                                    [styles.leaving]: slide !== currentSlide,
                                },
                            ])}
                        >
                            <SlideComponent
                                {...slide}
                                form={form}
                                className={classNames([styles.slide, styles[type]])}
                                onIdentify={onIdentify}
                                onCompleted={() => onSlideCompleted(slide)}
                                onAnswer={(answer) => onSlideAnswer(slide, answer)}
                                gotoPreviousSlide={() => gotoPreviousSlide(slide)}
                                subscriptions={subscriptions}
                            />
                        </animated.div>
                    ) : null;
                })}
                <div className={styles.images} ref={imagesRef}>
                    {imageTransitions((style, image) => {
                        const imageUrl = formatImageUrl(getImageUrl(image, 'large'));
                        return imageUrl !== null ? (
                            <animated.div style={style} className={styles.image}>
                                <img src={imageUrl} />
                            </animated.div>
                        ) : null;
                    })}
                </div>
            </div>
        </div>
    );
}

export default Survey;
